import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { systemConfigService } from '../../services/systemConfigService';
import {
  SystemConfig as SystemConfigType,
  RegistrationConfig,
  ContentConfig,
  EmailConfig,
  SecurityConfig,
  FeatureFlags,
  TimelineConfig,
  LookAndFeelConfig,
} from '../../types/admin';
import { useUser } from '../../contexts/AuthContext';
import { LookAndFeelTab } from '../../components/admin/LookAndFeelTab';

/**
 * SystemConfig Component
 * Page for managing all system configuration settings
 *
 * Features:
 * - Tabbed interface for different config sections
 * - Registration settings
 * - Content settings
 * - Email settings
 * - Security settings
 * - Feature flags
 * - Maintenance mode
 * - Export/import configuration
 * - Form validation
 * - Audit logging
 */
const SystemConfig: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useUser();

  // State
  const [config, setConfig] = useState<SystemConfigType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Temporary state for form edits
  const [formData, setFormData] = useState<SystemConfigType | null>(null);

  /**
   * Load configuration on mount
   */
  useEffect(() => {
    loadConfig();
  }, []);

  /**
   * Load system configuration
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const loadedConfig = await systemConfigService.getConfig();
      setConfig(loadedConfig);
      setFormData(JSON.parse(JSON.stringify(loadedConfig))); // Deep clone
      setHasChanges(false);
    } catch (err: any) {
      console.error('Error loading config:', err);
      setError(err.message || 'Failed to load system configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update form data
   */
  const updateFormData = (section: keyof SystemConfigType, field: string, value: any) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [section]: {
        ...(formData[section] as any),
        [field]: value,
      },
    });
    setHasChanges(true);
  };

  /**
   * Handle save configuration
   */
  const handleSave = async () => {
    if (!formData || !currentUser?.uid) return;

    try {
      setSaving(true);
      setError(null);

      // Validate configuration
      const validation = systemConfigService.validateConfig(formData);
      if (!validation.valid) {
        showSnackbar(`Validation errors: ${validation.errors.join(', ')}`, 'error');
        return;
      }

      await systemConfigService.updateConfig(formData, currentUser.uid, currentUser.displayName);

      // Reload config to get the updated version with displayName
      const updatedConfig = await systemConfigService.getConfig();
      setConfig(updatedConfig);
      setFormData(JSON.parse(JSON.stringify(updatedConfig)));
      setHasChanges(false);
      showSnackbar('Configuration saved successfully', 'success');
    } catch (err: any) {
      console.error('Error saving config:', err);
      showSnackbar(err.message || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle discard changes
   */
  const handleDiscard = () => {
    if (config) {
      setFormData(JSON.parse(JSON.stringify(config))); // Deep clone
      setHasChanges(false);
      showSnackbar('Changes discarded', 'info');
    }
  };

  /**
   * Handle export configuration
   */
  const handleExport = () => {
    if (!config) return;

    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `system-config-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);

    showSnackbar('Configuration exported successfully', 'success');
  };

  /**
   * Handle import configuration
   */
  const handleImportOpen = () => {
    setImportDialog(true);
    setImportJson('');
  };

  /**
   * Handle import confirm
   */
  const handleImportConfirm = async () => {
    try {
      const importedConfig = JSON.parse(importJson) as SystemConfigType;

      // Validate imported configuration
      const validation = systemConfigService.validateConfig(importedConfig);
      if (!validation.valid) {
        showSnackbar(`Invalid configuration: ${validation.errors.join(', ')}`, 'error');
        return;
      }

      setFormData(importedConfig);
      setHasChanges(true);
      setImportDialog(false);
      showSnackbar('Configuration imported successfully (not saved yet)', 'success');
    } catch (err: any) {
      console.error('Error importing config:', err);
      showSnackbar('Invalid JSON format', 'error');
    }
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Close snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!formData) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Failed to load configuration</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                System Configuration
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage all system settings and configurations
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadConfig}
                disabled={loading || saving}
              >
                Reload
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                disabled={loading || saving}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<ImportIcon />}
                onClick={handleImportOpen}
                disabled={loading || saving}
              >
                Import
              </Button>
            </Box>
          </Box>

          {/* Save/Discard Actions */}
          {hasChanges && (
            <Paper sx={{ p: 2, bgcolor: 'warning.light', mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon />
                  <Typography fontWeight={600}>You have unsaved changes</Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Button variant="outlined" onClick={handleDiscard} disabled={saving}>
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Last Updated Info */}
        {config && config.updatedAt && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {new Date(config.updatedAt).toLocaleString()} by{' '}
              {config.updatedByDisplayName || config.updatedBy}
            </Typography>
          </Box>
        )}

        {/* Configuration Tabs */}
        <Paper>
          <Tabs
            value={activeTab}
            onChange={(e, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Registration" />
            <Tab label="Content" />
            <Tab label="Email" />
            <Tab label="Security" />
            <Tab label="Features" />
            <Tab label="Timeline" />
            <Tab label="Maintenance" />
            <Tab label="Look & Feel" />
            <Tab label="Metadata" />
          </Tabs>

          <Box p={3}>
            {/* Registration Settings */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Registration Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.registration.enabled}
                        onChange={(e) =>
                          updateFormData('registration', 'enabled', e.target.checked)
                        }
                      />
                    }
                    label="Enable User Registration"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow new users to register accounts
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.registration.requireFamilyCode}
                        onChange={(e) =>
                          updateFormData('registration', 'requireFamilyCode', e.target.checked)
                        }
                      />
                    }
                    label="Require Family Code"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Require users to enter a family code during registration
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.registration.autoApprove}
                        onChange={(e) =>
                          updateFormData('registration', 'autoApprove', e.target.checked)
                        }
                      />
                    }
                    label="Auto-Approve Registrations"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Automatically approve new registrations without admin review
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.registration.requireEmailVerification}
                        onChange={(e) =>
                          updateFormData(
                            'registration',
                            'requireEmailVerification',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Require Email Verification"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Require users to verify their email before full access
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Role</InputLabel>
                    <Select
                      value={formData.registration.defaultRole}
                      onChange={(e) =>
                        updateFormData('registration', 'defaultRole', e.target.value)
                      }
                      label="Default Role"
                    >
                      <MenuItem value="viewer">Viewer</MenuItem>
                      <MenuItem value="contributor">Contributor</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Role assigned to new users after approval
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Welcome Message"
                    multiline
                    rows={3}
                    value={formData.registration.welcomeMessage || ''}
                    onChange={(e) =>
                      updateFormData('registration', 'welcomeMessage', e.target.value)
                    }
                    helperText="Message shown to users after successful registration"
                  />
                </Grid>
              </Grid>
            )}

            {/* Content Settings */}
            {activeTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Content Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                {/* Photo Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Photo Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Photo Size (MB)"
                    value={formData.content.maxPhotoSizeMB}
                    onChange={(e) =>
                      updateFormData('content', 'maxPhotoSizeMB', parseInt(e.target.value))
                    }
                    helperText="Maximum file size for photo uploads"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Allowed Photo Types"
                    value={(formData.content.allowedPhotoTypes || []).join(', ')}
                    onChange={(e) =>
                      updateFormData(
                        'content',
                        'allowedPhotoTypes',
                        e.target.value.split(',').map((s) => s.trim())
                      )
                    }
                    helperText="Comma-separated list (e.g., jpg, png, gif)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Photos Per Upload"
                    value={formData.content.photoUploadLimit}
                    onChange={(e) =>
                      updateFormData('content', 'photoUploadLimit', parseInt(e.target.value))
                    }
                    helperText="Maximum number of photos in a single upload"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.requirePhotoApproval}
                        onChange={(e) =>
                          updateFormData('content', 'requirePhotoApproval', e.target.checked)
                        }
                      />
                    }
                    label="Require Photo Approval"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Photos need admin approval before being visible
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableComments}
                        onChange={(e) =>
                          updateFormData('content', 'enableComments', e.target.checked)
                        }
                      />
                    }
                    label="Enable Comments"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to comment on profiles and photos
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enablePhotoTagging}
                        onChange={(e) =>
                          updateFormData('content', 'enablePhotoTagging', e.target.checked)
                        }
                      />
                    }
                    label="Enable Photo Tagging"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to tag people in photos
                  </Typography>
                </Grid>

                {/* Document Upload Settings */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Document Upload Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Document Size (MB)"
                    value={formData.content.maxDocumentSizeMB}
                    onChange={(e) =>
                      updateFormData('content', 'maxDocumentSizeMB', parseInt(e.target.value))
                    }
                    helperText="Maximum file size for document uploads"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Allowed Document Types"
                    value={(formData.content.allowedDocumentTypes || []).join(', ')}
                    onChange={(e) =>
                      updateFormData(
                        'content',
                        'allowedDocumentTypes',
                        e.target.value.split(',').map((s) => s.trim())
                      )
                    }
                    helperText="Comma-separated list (e.g., pdf, jpg, png, tiff)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Document Upload Limit"
                    value={formData.content.documentUploadLimit}
                    onChange={(e) =>
                      updateFormData('content', 'documentUploadLimit', parseInt(e.target.value))
                    }
                    helperText="Max documents per upload batch"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Documents Per Person"
                    value={formData.content.maxDocumentsPerPerson}
                    onChange={(e) =>
                      updateFormData('content', 'maxDocumentsPerPerson', parseInt(e.target.value))
                    }
                    helperText="Max documents per person (0 = unlimited)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.requireDocumentApproval}
                        onChange={(e) =>
                          updateFormData('content', 'requireDocumentApproval', e.target.checked)
                        }
                      />
                    }
                    label="Require Document Approval"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Documents need admin approval before being visible
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableDocumentTranscription}
                        onChange={(e) =>
                          updateFormData('content', 'enableDocumentTranscription', e.target.checked)
                        }
                      />
                    }
                    label="Enable Document Transcription"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to transcribe documents
                  </Typography>
                </Grid>

                {/* Memory Settings */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Memory & Story Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Memory Audio Size (MB)"
                    value={formData.content.maxMemoryAudioSizeMB}
                    onChange={(e) =>
                      updateFormData('content', 'maxMemoryAudioSizeMB', parseInt(e.target.value))
                    }
                    helperText="Maximum audio file size"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Memory Video Size (MB)"
                    value={formData.content.maxMemoryVideoSizeMB}
                    onChange={(e) =>
                      updateFormData('content', 'maxMemoryVideoSizeMB', parseInt(e.target.value))
                    }
                    helperText="Maximum video file size"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Allowed Audio Types"
                    value={(formData.content.allowedMemoryAudioTypes || []).join(', ')}
                    onChange={(e) =>
                      updateFormData(
                        'content',
                        'allowedMemoryAudioTypes',
                        e.target.value.split(',').map((s) => s.trim())
                      )
                    }
                    helperText="Comma-separated list (e.g., mp3, m4a, wav)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Allowed Video Types"
                    value={(formData.content.allowedMemoryVideoTypes || []).join(', ')}
                    onChange={(e) =>
                      updateFormData(
                        'content',
                        'allowedMemoryVideoTypes',
                        e.target.value.split(',').map((s) => s.trim())
                      )
                    }
                    helperText="Comma-separated list (e.g., mp4, mov)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Memories Per Person"
                    value={formData.content.maxMemoriesPerPerson}
                    onChange={(e) =>
                      updateFormData('content', 'maxMemoriesPerPerson', parseInt(e.target.value))
                    }
                    helperText="Max memories per person (0 = unlimited)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Memory Text Length"
                    value={formData.content.maxMemoryTextLength}
                    onChange={(e) =>
                      updateFormData('content', 'maxMemoryTextLength', parseInt(e.target.value))
                    }
                    helperText="Maximum characters for memory text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.requireMemoryApproval}
                        onChange={(e) =>
                          updateFormData('content', 'requireMemoryApproval', e.target.checked)
                        }
                      />
                    }
                    label="Require Memory Approval"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Memories need admin approval before being visible
                  </Typography>
                </Grid>

                {/* Notes Settings */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Notes Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enablePrivateNotes}
                        onChange={(e) =>
                          updateFormData('content', 'enablePrivateNotes', e.target.checked)
                        }
                      />
                    }
                    label="Enable Private Notes"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to create private notes (only visible to author)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enablePublicNotes}
                        onChange={(e) =>
                          updateFormData('content', 'enablePublicNotes', e.target.checked)
                        }
                      />
                    }
                    label="Enable Public Notes"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to create public notes (visible to all)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.requirePublicNoteApproval}
                        onChange={(e) =>
                          updateFormData('content', 'requirePublicNoteApproval', e.target.checked)
                        }
                      />
                    }
                    label="Require Public Note Approval"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Public notes need admin approval before being visible
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Notes Per Person"
                    value={formData.content.maxNotesPerPerson}
                    onChange={(e) =>
                      updateFormData('content', 'maxNotesPerPerson', parseInt(e.target.value))
                    }
                    helperText="Max notes per person (0 = unlimited)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Note Length"
                    value={formData.content.maxNoteLength}
                    onChange={(e) =>
                      updateFormData('content', 'maxNoteLength', parseInt(e.target.value))
                    }
                    helperText="Maximum characters for notes"
                  />
                </Grid>

                {/* Comment Settings */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Comment Settings (Profile Comments Tab)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableCommentThreading}
                        onChange={(e) =>
                          updateFormData('content', 'enableCommentThreading', e.target.checked)
                        }
                      />
                    }
                    label="Enable Comment Threading"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to reply to comments
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Comment Thread Depth"
                    value={formData.content.maxCommentThreadDepth}
                    onChange={(e) =>
                      updateFormData('content', 'maxCommentThreadDepth', parseInt(e.target.value))
                    }
                    helperText="Maximum nesting level for comment replies"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableCommentReactions}
                        onChange={(e) =>
                          updateFormData('content', 'enableCommentReactions', e.target.checked)
                        }
                      />
                    }
                    label="Enable Comment Reactions"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to add emoji reactions to comments
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableCommentMentions}
                        onChange={(e) =>
                          updateFormData('content', 'enableCommentMentions', e.target.checked)
                        }
                      />
                    }
                    label="Enable @Mentions"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to mention others with @username
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Comment Edit Window (Minutes)"
                    value={formData.content.commentEditWindowMinutes}
                    onChange={(e) =>
                      updateFormData('content', 'commentEditWindowMinutes', parseInt(e.target.value))
                    }
                    helperText="How long users can edit their comments"
                  />
                </Grid>

                {/* Research Notes Settings */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Research Notes Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableResearchNotes}
                        onChange={(e) =>
                          updateFormData('content', 'enableResearchNotes', e.target.checked)
                        }
                      />
                    }
                    label="Enable Research Notes"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to create research notes
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Research Notes Per Person"
                    value={formData.content.maxResearchNotesPerPerson}
                    onChange={(e) =>
                      updateFormData('content', 'maxResearchNotesPerPerson', parseInt(e.target.value))
                    }
                    helperText="Max research notes per person (0 = unlimited)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.requireResearchNoteApproval}
                        onChange={(e) =>
                          updateFormData('content', 'requireResearchNoteApproval', e.target.checked)
                        }
                      />
                    }
                    label="Require Research Note Approval"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Research notes need admin approval before being visible
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.content.enableSourceCitations}
                        onChange={(e) =>
                          updateFormData('content', 'enableSourceCitations', e.target.checked)
                        }
                      />
                    }
                    label="Enable Source Citations"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to add source citations to research notes
                  </Typography>
                </Grid>
              </Grid>
            )}

            {/* Email Settings */}
            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Email Notification Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.sendWelcomeEmail}
                        onChange={(e) =>
                          updateFormData('email', 'sendWelcomeEmail', e.target.checked)
                        }
                      />
                    }
                    label="Send Welcome Email"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Send welcome email to newly approved users
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.sendApprovalNotification}
                        onChange={(e) =>
                          updateFormData('email', 'sendApprovalNotification', e.target.checked)
                        }
                      />
                    }
                    label="Send Approval Notifications"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Notify users when their account is approved
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.sendRejectionNotification}
                        onChange={(e) =>
                          updateFormData('email', 'sendRejectionNotification', e.target.checked)
                        }
                      />
                    }
                    label="Send Rejection Notifications"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Notify users when their registration is rejected
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.sendContentNotifications}
                        onChange={(e) =>
                          updateFormData('email', 'sendContentNotifications', e.target.checked)
                        }
                      />
                    }
                    label="Send Content Notifications"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Notify users about comments, tags, and updates
                  </Typography>
                </Grid>

                {/* New Profile Tabs Notifications */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Profile Tabs Notifications
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnDocumentUpload}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnDocumentUpload', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Document Upload"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when new document uploaded
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnMemoryPublished}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnMemoryPublished', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Memory Published"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when new memory published
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnPublicNoteAdded}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnPublicNoteAdded', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Public Note Added"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when public note added
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnCommentMention}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnCommentMention', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Comment Mention (CRITICAL)"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when mentioned in comment (@username)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnCommentReply}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnCommentReply', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Comment Reply"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when someone replies to comment
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.email.notifyOnResearchNoteUpdate}
                        onChange={(e) =>
                          updateFormData('email', 'notifyOnResearchNoteUpdate', e.target.checked)
                        }
                      />
                    }
                    label="Notify on Research Note Update"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Email when research note updated
                  </Typography>
                </Grid>

                {/* Email Configuration */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Email Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="From Email Address"
                    value={formData.email.fromEmail}
                    onChange={(e) => updateFormData('email', 'fromEmail', e.target.value)}
                    helperText="Email address shown as sender"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="From Name"
                    value={formData.email.fromName}
                    onChange={(e) => updateFormData('email', 'fromName', e.target.value)}
                    helperText="Name shown as sender"
                  />
                </Grid>
              </Grid>
            )}

            {/* Security Settings */}
            {activeTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Security Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Password Length"
                    value={formData.security.passwordMinLength}
                    onChange={(e) =>
                      updateFormData('security', 'passwordMinLength', parseInt(e.target.value))
                    }
                    helperText="Minimum required password length"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.security.requireStrongPassword}
                        onChange={(e) =>
                          updateFormData('security', 'requireStrongPassword', e.target.checked)
                        }
                      />
                    }
                    label="Require Strong Passwords"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Require uppercase, lowercase, numbers, and special characters
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Session Timeout (minutes)"
                    value={formData.security.sessionTimeoutMinutes}
                    onChange={(e) =>
                      updateFormData('security', 'sessionTimeoutMinutes', parseInt(e.target.value))
                    }
                    helperText="Auto-logout after this period of inactivity"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Login Attempts"
                    value={formData.security.maxLoginAttempts}
                    onChange={(e) =>
                      updateFormData('security', 'maxLoginAttempts', parseInt(e.target.value))
                    }
                    helperText="Lock account after this many failed attempts"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Lockout Duration (minutes)"
                    value={formData.security.lockoutDurationMinutes}
                    onChange={(e) =>
                      updateFormData('security', 'lockoutDurationMinutes', parseInt(e.target.value))
                    }
                    helperText="How long accounts remain locked after max attempts"
                  />
                </Grid>
              </Grid>
            )}

            {/* Feature Flags */}
            {activeTab === 4 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Feature Flags
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableFamilyTree}
                        onChange={(e) =>
                          updateFormData('features', 'enableFamilyTree', e.target.checked)
                        }
                      />
                    }
                    label="Enable Family Tree Viewer"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to view interactive family trees
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enablePhotoGallery}
                        onChange={(e) =>
                          updateFormData('features', 'enablePhotoGallery', e.target.checked)
                        }
                      />
                    }
                    label="Enable Photo Gallery"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to browse and upload photos
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableTimeline}
                        onChange={(e) =>
                          updateFormData('features', 'enableTimeline', e.target.checked)
                        }
                      />
                    }
                    label="Enable Timeline View"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Display events in chronological timeline format
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableExport}
                        onChange={(e) =>
                          updateFormData('features', 'enableExport', e.target.checked)
                        }
                      />
                    }
                    label="Enable Data Export"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to export data to GEDCOM and CSV
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableSearch}
                        onChange={(e) =>
                          updateFormData('features', 'enableSearch', e.target.checked)
                        }
                      />
                    }
                    label="Enable Advanced Search"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow advanced search with multiple criteria
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableReports}
                        onChange={(e) =>
                          updateFormData('features', 'enableReports', e.target.checked)
                        }
                      />
                    }
                    label="Enable Reports"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow generation of family reports and charts
                  </Typography>
                </Grid>

                {/* Profile Tabs Feature Flags */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Profile Tabs Features
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableDocumentsTab}
                        onChange={(e) =>
                          updateFormData('features', 'enableDocumentsTab', e.target.checked)
                        }
                      />
                    }
                    label="Enable Documents Tab"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show documents tab on person profiles
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableMemoriesTab}
                        onChange={(e) =>
                          updateFormData('features', 'enableMemoriesTab', e.target.checked)
                        }
                      />
                    }
                    label="Enable Memories & Stories Tab"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show memories & stories tab on person profiles
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableNotesTab}
                        onChange={(e) =>
                          updateFormData('features', 'enableNotesTab', e.target.checked)
                        }
                      />
                    }
                    label="Enable Notes Tab"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show notes tab on person profiles
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableCommentsTab}
                        onChange={(e) =>
                          updateFormData('features', 'enableCommentsTab', e.target.checked)
                        }
                      />
                    }
                    label="Enable Comments Tab"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show comments tab on person profiles
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableResearchTab}
                        onChange={(e) =>
                          updateFormData('features', 'enableResearchTab', e.target.checked)
                        }
                      />
                    }
                    label="Enable Research Notes Tab"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show research notes tab on person profiles
                  </Typography>
                </Grid>

                {/* Master Timeline Feature Flag (Phase 3.3) */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Master Timeline Feature
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features.enableMasterTimeline}
                        onChange={(e) =>
                          updateFormData('features', 'enableMasterTimeline', e.target.checked)
                        }
                      />
                    }
                    label="Enable Master Timeline Page"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Show master timeline page with all family events across all people
                  </Typography>
                </Grid>
              </Grid>
            )}

            {/* Timeline Settings */}
            {activeTab === 5 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Master Timeline Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Configure the master timeline page that shows all family events across all people
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                {/* Performance Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Performance Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Events to Display"
                    type="number"
                    value={formData.timeline.maxEventsToDisplay}
                    onChange={(e) =>
                      updateFormData('timeline', 'maxEventsToDisplay', parseInt(e.target.value) || 0)
                    }
                    helperText="Maximum events to load at once (0 = unlimited)"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Virtualization Threshold"
                    type="number"
                    value={formData.timeline.virtualizationThreshold}
                    onChange={(e) =>
                      updateFormData('timeline', 'virtualizationThreshold', parseInt(e.target.value) || 100)
                    }
                    helperText="Number of events before enabling virtualization"
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enableGenerationCalculation}
                        onChange={(e) =>
                          updateFormData('timeline', 'enableGenerationCalculation', e.target.checked)
                        }
                      />
                    }
                    label="Enable Generation Calculation"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Calculate and display generation numbers for timeline events
                  </Typography>
                </Grid>

                {/* Default View Settings */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Default View Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default View Mode</InputLabel>
                    <Select
                      value={formData.timeline.defaultViewMode}
                      label="Default View Mode"
                      onChange={(e) =>
                        updateFormData('timeline', 'defaultViewMode', e.target.value)
                      }
                    >
                      <MenuItem value="chronological">Chronological</MenuItem>
                      <MenuItem value="generation-grouped">Generation Grouped</MenuItem>
                      <MenuItem value="timeline-plot">Timeline Plot</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Default timeline view when page loads
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Sort Order</InputLabel>
                    <Select
                      value={formData.timeline.defaultSortOrder}
                      label="Default Sort Order"
                      onChange={(e) =>
                        updateFormData('timeline', 'defaultSortOrder', e.target.value)
                      }
                    >
                      <MenuItem value="asc">Oldest First (Ascending)</MenuItem>
                      <MenuItem value="desc">Newest First (Descending)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Default sort order for chronological view
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Date Range</InputLabel>
                    <Select
                      value={formData.timeline.defaultDateRange}
                      label="Default Date Range"
                      onChange={(e) =>
                        updateFormData('timeline', 'defaultDateRange', e.target.value)
                      }
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="1700s">1700s</MenuItem>
                      <MenuItem value="1800s">1800s</MenuItem>
                      <MenuItem value="1900s">1900s</MenuItem>
                      <MenuItem value="2000s">2000s</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Default date range filter when page loads
                  </Typography>
                </Grid>

                {/* Export Settings */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Export Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.allowExports}
                        onChange={(e) =>
                          updateFormData('timeline', 'allowExports', e.target.checked)
                        }
                      />
                    }
                    label="Allow Exports"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Enable users to export timeline data
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.allowPdfExport}
                        onChange={(e) =>
                          updateFormData('timeline', 'allowPdfExport', e.target.checked)
                        }
                        disabled={!formData.timeline.allowExports}
                      />
                    }
                    label="Allow PDF Export"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Enable exporting timeline as PDF
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.allowCsvExport}
                        onChange={(e) =>
                          updateFormData('timeline', 'allowCsvExport', e.target.checked)
                        }
                        disabled={!formData.timeline.allowExports}
                      />
                    }
                    label="Allow CSV Export"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Enable exporting timeline as CSV spreadsheet
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.allowGedcomExport}
                        onChange={(e) =>
                          updateFormData('timeline', 'allowGedcomExport', e.target.checked)
                        }
                        disabled={!formData.timeline.allowExports}
                      />
                    }
                    label="Allow GEDCOM Export"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Enable exporting timeline in GEDCOM format
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Export Events"
                    type="number"
                    value={formData.timeline.maxExportEvents}
                    onChange={(e) =>
                      updateFormData('timeline', 'maxExportEvents', parseInt(e.target.value) || 0)
                    }
                    helperText="Maximum events per export (0 = unlimited)"
                    InputProps={{ inputProps: { min: 0 } }}
                    disabled={!formData.timeline.allowExports}
                  />
                </Grid>

                {/* Sharing Settings */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Sharing Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enableShareLinks}
                        onChange={(e) =>
                          updateFormData('timeline', 'enableShareLinks', e.target.checked)
                        }
                      />
                    }
                    label="Enable Share Links"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow users to generate shareable timeline links
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.shareLinksIncludeFilters}
                        onChange={(e) =>
                          updateFormData('timeline', 'shareLinksIncludeFilters', e.target.checked)
                        }
                        disabled={!formData.timeline.enableShareLinks}
                      />
                    }
                    label="Include Filters in Share Links"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Include current filter state in generated share links
                  </Typography>
                </Grid>

                {/* Display Settings */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Display Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.showEventIcons}
                        onChange={(e) =>
                          updateFormData('timeline', 'showEventIcons', e.target.checked)
                        }
                      />
                    }
                    label="Show Event Icons"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Display event type icons (birth, death, marriage, etc.)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.showPersonPhotos}
                        onChange={(e) =>
                          updateFormData('timeline', 'showPersonPhotos', e.target.checked)
                        }
                      />
                    }
                    label="Show Person Photos"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Display person photos in event cards
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.showStatistics}
                        onChange={(e) =>
                          updateFormData('timeline', 'showStatistics', e.target.checked)
                        }
                      />
                    }
                    label="Show Statistics"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Display timeline statistics (event count, date range, etc.)
                  </Typography>
                </Grid>

                {/* Filter Settings */}
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Filter Options
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enablePersonFilter}
                        onChange={(e) =>
                          updateFormData('timeline', 'enablePersonFilter', e.target.checked)
                        }
                      />
                    }
                    label="Enable Person Filter"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow filtering events by specific people
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enableEventTypeFilter}
                        onChange={(e) =>
                          updateFormData('timeline', 'enableEventTypeFilter', e.target.checked)
                        }
                      />
                    }
                    label="Enable Event Type Filter"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow filtering by event type (birth, death, marriage, etc.)
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enableDateRangeFilter}
                        onChange={(e) =>
                          updateFormData('timeline', 'enableDateRangeFilter', e.target.checked)
                        }
                      />
                    }
                    label="Enable Date Range Filter"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow filtering events by date range
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.timeline.enableGenerationFilter}
                        onChange={(e) =>
                          updateFormData('timeline', 'enableGenerationFilter', e.target.checked)
                        }
                      />
                    }
                    label="Enable Generation Filter"
                  />
                  <Typography variant="body2" color="text.secondary" ml={4}>
                    Allow filtering events by generation number
                  </Typography>
                </Grid>
              </Grid>
            )}

            {/* Maintenance Mode */}
            {activeTab === 6 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Maintenance Mode
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ bgcolor: formData.maintenance.enabled ? 'error.light' : 'inherit' }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.maintenance.enabled}
                            onChange={(e) =>
                              updateFormData('maintenance', 'enabled', e.target.checked)
                            }
                          />
                        }
                        label="Enable Maintenance Mode"
                      />
                      <Typography variant="body2" color="text.secondary" ml={4}>
                        When enabled, the site will show a maintenance message to all users except
                        admins
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Maintenance Message"
                    multiline
                    rows={4}
                    value={formData.maintenance.message}
                    onChange={(e) => updateFormData('maintenance', 'message', e.target.value)}
                    helperText="Message displayed to users during maintenance"
                    disabled={!formData.maintenance.enabled}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Estimated End Time"
                    value={
                      formData.maintenance.estimatedEndTime
                        ? new Date(formData.maintenance.estimatedEndTime)
                            .toISOString()
                            .slice(0, 16)
                        : ''
                    }
                    onChange={(e) =>
                      updateFormData(
                        'maintenance',
                        'estimatedEndTime',
                        e.target.value ? new Date(e.target.value) : undefined
                      )
                    }
                    helperText="When maintenance is expected to end"
                    disabled={!formData.maintenance.enabled}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Look & Feel Settings */}
            {activeTab === 7 && (
              <LookAndFeelTab
                lookAndFeel={formData.lookAndFeel}
                onChange={(field, value) => updateFormData('lookAndFeel', field, value)}
              />
            )}

            {/* Metadata Settings */}
            {activeTab === 8 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Last Updated Metadata
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Configure visibility of "last updated" timestamps throughout the application.
                  </Typography>
                </Grid>

                {/* Global Toggle */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.metadata.showLastUpdated}
                        onChange={(e) => updateFormData('metadata', 'showLastUpdated', e.target.checked)}
                      />
                    }
                    label="Show Last Updated Timestamps"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Master toggle for displaying metadata across the application
                  </Typography>
                </Grid>

                {/* Roles That Can See Metadata */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Roles That Can See Metadata
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch checked disabled />}
                      label="Admin (always visible)"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showLastUpdatedTo.includes('editor')}
                          onChange={(e) => {
                            const roles = formData.metadata.showLastUpdatedTo.filter(r => r !== 'editor');
                            if (e.target.checked) roles.push('editor');
                            updateFormData('metadata', 'showLastUpdatedTo', roles);
                          }}
                        />
                      }
                      label="Editor"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showLastUpdatedTo.includes('contributor')}
                          onChange={(e) => {
                            const roles = formData.metadata.showLastUpdatedTo.filter(r => r !== 'contributor');
                            if (e.target.checked) roles.push('contributor');
                            updateFormData('metadata', 'showLastUpdatedTo', roles);
                          }}
                        />
                      }
                      label="Contributor"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showLastUpdatedTo.includes('viewer')}
                          onChange={(e) => {
                            const roles = formData.metadata.showLastUpdatedTo.filter(r => r !== 'viewer');
                            if (e.target.checked) roles.push('viewer');
                            updateFormData('metadata', 'showLastUpdatedTo', roles);
                          }}
                        />
                      }
                      label="Viewer"
                    />
                  </FormGroup>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Select which user roles can see metadata when it's enabled
                  </Typography>
                </Grid>

                {/* Show Author Name */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.metadata.showLastUpdatedBy}
                        onChange={(e) => updateFormData('metadata', 'showLastUpdatedBy', e.target.checked)}
                      />
                    }
                    label="Show Author Name in Metadata"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Display the name of the person who made the last update
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Toggle Button Settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Metadata Toggle Button
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.metadata.showToggleButton}
                        onChange={(e) => updateFormData('metadata', 'showToggleButton', e.target.checked)}
                      />
                    }
                    label="Show Toggle Button in UI"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Display a floating action button for quick metadata toggle
                  </Typography>
                </Grid>

                {/* Roles That Can See Toggle Button */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Roles That Can See Toggle Button
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showToggleButtonTo.includes('admin')}
                          onChange={(e) => {
                            const roles = formData.metadata.showToggleButtonTo.filter(r => r !== 'admin');
                            if (e.target.checked) roles.push('admin');
                            updateFormData('metadata', 'showToggleButtonTo', roles);
                          }}
                        />
                      }
                      label="Admin"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showToggleButtonTo.includes('editor')}
                          onChange={(e) => {
                            const roles = formData.metadata.showToggleButtonTo.filter(r => r !== 'editor');
                            if (e.target.checked) roles.push('editor');
                            updateFormData('metadata', 'showToggleButtonTo', roles);
                          }}
                        />
                      }
                      label="Editor"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showToggleButtonTo.includes('contributor')}
                          onChange={(e) => {
                            const roles = formData.metadata.showToggleButtonTo.filter(r => r !== 'contributor');
                            if (e.target.checked) roles.push('contributor');
                            updateFormData('metadata', 'showToggleButtonTo', roles);
                          }}
                        />
                      }
                      label="Contributor"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.metadata.showToggleButtonTo.includes('viewer')}
                          onChange={(e) => {
                            const roles = formData.metadata.showToggleButtonTo.filter(r => r !== 'viewer');
                            if (e.target.checked) roles.push('viewer');
                            updateFormData('metadata', 'showToggleButtonTo', roles);
                          }}
                        />
                      }
                      label="Viewer"
                    />
                  </FormGroup>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Select which user roles can see and use the metadata toggle button
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Display Format */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Display Format</InputLabel>
                    <Select
                      value={formData.metadata.displayFormat}
                      label="Display Format"
                      onChange={(e) => updateFormData('metadata', 'displayFormat', e.target.value)}
                    >
                      <MenuItem value="short">Short (2h ago, 3d ago)</MenuItem>
                      <MenuItem value="long">Long (Nov 10, 2025 3:45 PM)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Choose how timestamps are displayed throughout the application
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </Paper>

        {/* Import Configuration Dialog */}
        <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Import Configuration</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Paste the exported JSON configuration below. This will replace all current settings
              when you click Import and then Save.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder="Paste JSON configuration here..."
              sx={{ fontFamily: 'monospace' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialog(false)}>Cancel</Button>
            <Button onClick={handleImportConfirm} color="primary" variant="contained">
              Import
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SystemConfig;
